// Inspetor da aba Final: corrige palavra da legenda (erro de transcrição), texto e
// linhas do gancho (+ troca por outra opção da IA), encaixe da imagem, animações da
// biblioteca (template, textos/números pelo schema, zona, remover, adicionar na agulha)
// e itens "atrás da pessoa"; sem seleção, troca rápida de estilo (reaplica pela Fase 2)
// e cor de destaque (local, preview instantâneo).
import { useState } from 'react';
import type { CaptionStyleId, EditStyleId, HeadlineStyleId, MotionGraphic, StyleSetup } from '../../../types/videoEdit';
import {
  GRAPHIC_MAX_COUNT,
  GRAPHIC_SPECS,
  GRAPHIC_TEMPLATES,
  convertGraphicProps,
  isGraphicTemplate,
  normalizeGraphicHex,
  type GraphicField,
  type GraphicTemplateId,
} from '../../../video-compositions/graphics/schema';
import type { VisualProps } from './demoProps';
import { twoLines } from './demoProps';
import type { CaptionLine, FinalSel } from './FinalTimeline';
import { CAPTION_OPTIONS, EDIT_OPTIONS, HEADLINE_OPTIONS } from './styleCatalog';
import { fmt, normHex } from './edvidTime';
import { BTN, INPUT, LABEL, Panel } from './ui';

export function FinalInspector({ props, sel, lines, style, onBegin, onGesture, onEnd, onCommit, onRestyle, onSelect, restyling, now, hookOptions }: {
  props: VisualProps;
  sel: FinalSel;
  lines: CaptionLine[];
  style: StyleSetup;
  onBegin: () => void;
  onGesture: (fn: (p: VisualProps) => VisualProps) => void;
  onEnd: () => void;
  onCommit: (fn: (p: VisualProps) => VisualProps) => void;
  onRestyle: (style: StyleSetup) => void;
  onSelect: (s: FinalSel) => void;
  restyling: boolean;
  /** posição da agulha (s) — "+ adicionar animação" entra aqui */
  now: () => number;
  /** outras headlines geradas pela IA (troca com 1 clique) */
  hookOptions?: string[];
}) {
  const ed = props.editData;
  const [quick, setQuick] = useState<Pick<StyleSetup, 'edit' | 'headline' | 'captions'>>({ edit: style.edit, headline: style.headline, captions: style.captions });
  const quickChanged = quick.edit !== style.edit || quick.headline !== style.headline || quick.captions !== style.captions;
  const accent = ed.hook.accent || ed.captions.accent || style.accent;
  // campo de texto: um gesto por foco (o Ctrl+Z desfaz a edição inteira do campo, não letra a letra)
  const field = { onFocus: onBegin, onBlur: onEnd };

  if (sel?.kind === 'caption') {
    const line = lines[sel.line];
    if (!line) return null;
    return (
      <Panel title={`Legenda · ${fmt(line.start)} → ${fmt(line.end)}`} right={<button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => onSelect(null)}>fechar</button>}>
        <p className="mb-2 text-[11px] text-slate-500">Corrija uma palavra transcrita errada. O tempo de cada palavra não muda.</p>
        <div className="flex flex-wrap gap-1.5">
          {line.words.map(wi => (
            <input
              key={wi}
              value={props.captions[wi]?.text ?? ''}
              {...field}
              onChange={e => {
                const text = e.target.value;
                onGesture(p => {
                  const caps = [...p.captions];
                  caps[wi] = { ...caps[wi], text };
                  return { ...p, captions: caps };
                });
              }}
              className="w-auto min-w-[64px] rounded-lg border border-white/10 bg-slate-950 px-2 py-1.5 text-sm font-bold text-slate-100 outline-none focus:border-teal-400"
              style={{ width: `${Math.max(4, (props.captions[wi]?.text.length || 1) + 2)}ch` }}
            />
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <button type="button" className={BTN} disabled={sel.line <= 0} onClick={() => onSelect({ kind: 'caption', line: sel.line - 1 })}>← anterior</button>
          <button type="button" className={BTN} disabled={sel.line >= lines.length - 1} onClick={() => onSelect({ kind: 'caption', line: sel.line + 1 })}>próxima →</button>
        </div>
      </Panel>
    );
  }

  if (sel?.kind === 'hook') {
    const hookLines = ed.hook.lines?.length ? ed.hook.lines : twoLines(ed.hook.text || '');
    const setLines = (next: string[]) => onGesture(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, lines: next, text: next.join(' ') } } }));
    return (
      <Panel title="Gancho" right={<button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => onSelect(null)}>fechar</button>}>
        <label className={LABEL}>Texto (reparte em 2 linhas)</label>
        <input
          className={`${INPUT} mt-1`}
          value={ed.hook.text || hookLines.join(' ')}
          {...field}
          onChange={e => { const text = e.target.value; onGesture(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, text, lines: twoLines(text) } } })); }}
        />
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          {[0, 1].map(i => (
            <div key={i}>
              <label className={LABEL}>Linha {i + 1}</label>
              <input className={`${INPUT} mt-1`} value={hookLines[i] || ''} {...field} onChange={e => { const next = [...hookLines]; next[i] = e.target.value; setLines(next.slice(0, 2)); }} />
            </div>
          ))}
        </div>
        {!!hookOptions?.length && (
          <div className="mt-3">
            <label className={LABEL}>Outras opções da IA</label>
            <div className="mt-1 flex flex-col gap-1.5">
              {hookOptions.map((opt, i) => {
                const current = (ed.hook.text || hookLines.join(' ')).trim() === opt.trim();
                return (
                  <button
                    key={i}
                    type="button"
                    data-testid="hook-option"
                    onClick={() => onCommit(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, text: opt, lines: twoLines(opt) } } }))}
                    className={`rounded-xl border px-3 py-2 text-left text-xs font-bold transition-colors ${current ? 'border-indigo-400 bg-indigo-500/15 text-indigo-100' : 'border-white/10 text-slate-300 hover:border-indigo-400/60 hover:bg-white/5'}`}
                    title={current ? 'headline atual' : 'usar esta headline'}
                  >
                    {opt}{current ? <span className="ml-2 text-[10px] font-black uppercase tracking-wider text-indigo-300">atual</span> : null}
                  </button>
                );
              })}
            </div>
          </div>
        )}
        <div className="mt-3 flex items-center gap-3">
          <label className={LABEL}>Sai em</label>
          <input
            type="number" step={0.1} min={0.5} max={ed.durationSec}
            className="w-24 rounded-xl border border-white/10 bg-slate-950 px-2 py-1.5 font-mono text-xs text-slate-100 outline-none focus:border-indigo-500"
            value={ed.hook.endSec}
            onChange={e => { const v = Math.max(0.5, Math.min(ed.durationSec, Number(e.target.value) || 0)); onCommit(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, endSec: v } } })); }}
          />
          <span className="text-[11px] text-slate-500">s (ou arraste a borda do chip)</span>
        </div>
      </Panel>
    );
  }

  if (sel?.kind === 'graphic') {
    const g = ed.graphics?.[sel.index];
    if (!g) return null;
    return (
      <GraphicInspector
        g={g}
        index={sel.index}
        duration={ed.durationSec}
        hookEnd={ed.hook.enabled ? ed.hook.endSec : 0}
        videoAccent={accent}
        field={field}
        onGesture={onGesture}
        onCommit={onCommit}
        onSelect={onSelect}
        now={now}
      />
    );
  }

  if (sel?.kind === 'behind') {
    const b = ed.behind?.[sel.index];
    if (!b) return null;
    const remove = () => {
      onCommit(p => ({ ...p, editData: { ...p.editData, behind: (p.editData.behind || []).filter((_, i) => i !== sel.index) } }));
      onSelect(null);
    };
    return (
      <Panel title={b.kind === 'words' ? 'Palavras atrás da pessoa' : 'Imagem atrás da pessoa'} right={<button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => onSelect(null)}>fechar</button>}>
        <div className="flex gap-3">
          {b.kind === 'image'
            ? <img src={b.src} alt="" className="h-20 w-20 shrink-0 rounded-xl border border-white/10 object-cover" />
            : <div className="flex h-20 w-20 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-slate-900 p-1 text-center text-[11px] font-black uppercase leading-tight text-sky-200">{b.words.map(w => w.t).join(' ')}</div>}
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-[11px] text-slate-300">{fmt(b.start)} → {fmt(b.start + b.dur)} ({b.dur.toFixed(2)}s)</p>
            <p className="text-[11px] text-slate-500">Fica entre o fundo e a pessoa recortada. Arraste o chip para mover; as bordas aparam.</p>
            <button type="button" onClick={remove} className="rounded-lg px-2 py-1 text-[11px] font-black text-rose-300 hover:bg-rose-500/10">Remover</button>
          </div>
        </div>
      </Panel>
    );
  }

  if (sel?.kind === 'split' || sel?.kind === 'insert') {
    const it = sel.kind === 'split' ? ed.splitInserts?.[sel.index] : ed.inserts[sel.index];
    if (!it) return null;
    const patchSplit = (patch: Record<string, unknown>) => onCommit(p => {
      const arr = [...(p.editData.splitInserts || [])];
      const group = arr[sel.index]?.group;
      const layout = patch.layout;
      for (let i = 0; i < arr.length; i++) {
        if (i === sel.index) arr[i] = { ...arr[i], ...patch };
        else if (group && arr[i].group === group && (layout === 'top' || layout === 'bottom')) arr[i] = { ...arr[i], layout };
      }
      const before = p.editData.splitInserts?.[sel.index];
      const windows = (layout === 'top' || layout === 'bottom') ? p.editData.captions.windows?.map(w => w.start === before?.start && w.end === before?.end ? { ...w, paddingBottom: layout === 'top' ? 1074 : 790 } : w) : undefined;
      return { ...p, editData: { ...p.editData, splitInserts: arr, captions: { ...p.editData.captions, ...(windows ? { windows } : {}) } } };
    });
    const remove = () => {
      onCommit(p => sel.kind === 'split'
        ? { ...p, editData: { ...p.editData, splitInserts: (p.editData.splitInserts || []).filter((_, i) => i !== sel.index) } }
        : { ...p, editData: { ...p.editData, inserts: p.editData.inserts.filter((_, i) => i !== sel.index) } });
      onSelect(null);
    };
    const split = sel.kind === 'split' ? ed.splitInserts?.[sel.index] : null;
    return (
      <Panel title={sel.kind === 'split' ? 'Imagem · tela dividida' : 'Imagem'} right={<button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => onSelect(null)}>fechar</button>}>
        <div className="flex gap-3">
          <img src={it.src} alt="" className="h-20 w-20 shrink-0 rounded-xl border border-white/10 object-cover" />
          <div className="min-w-0 space-y-2">
            <p className="font-mono text-[11px] text-slate-300">{fmt(it.start)} → {fmt(it.end)} ({(it.end - it.start).toFixed(2)}s)</p>
            {split && (
              <div className="flex flex-wrap gap-1.5">
                {(['top', 'bottom'] as const).map(l => (
                  <button key={l} type="button" onClick={() => patchSplit({ layout: l })} className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${(split.layout || 'top') === l ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-white/10 text-slate-400'}`}>{l === 'top' ? 'imagem em cima' : 'imagem embaixo'}</button>
                ))}
                {(['cover', 'contain'] as const).map(f => (
                  <button key={f} type="button" onClick={() => patchSplit({ fit: f })} className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${(split.fit || 'cover') === f ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-white/10 text-slate-400'}`}>{f === 'cover' ? 'preencher' : 'inteira'}</button>
                ))}
              </div>
            )}
            <button type="button" onClick={remove} className="rounded-lg px-2 py-1 text-[11px] font-black text-rose-300 hover:bg-rose-500/10">Remover imagem</button>
          </div>
        </div>
      </Panel>
    );
  }

  return (
    <Panel title="Estilo">
      <div className="grid gap-2 sm:grid-cols-3">
        <label className="space-y-1">
          <span className={LABEL}>Edição</span>
          <select className={`${INPUT} text-xs`} value={quick.edit} onChange={e => setQuick(q => ({ ...q, edit: e.target.value as EditStyleId }))}>
            {EDIT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className={LABEL}>Headline</span>
          <select className={`${INPUT} text-xs`} value={quick.headline} onChange={e => setQuick(q => ({ ...q, headline: e.target.value as HeadlineStyleId }))}>
            {HEADLINE_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
        <label className="space-y-1">
          <span className={LABEL}>Legenda</span>
          <select className={`${INPUT} text-xs`} value={quick.captions} onChange={e => setQuick(q => ({ ...q, captions: e.target.value as CaptionStyleId }))}>
            {CAPTION_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
          </select>
        </label>
      </div>
      {quickChanged && (
        <div className="mt-2 flex items-center gap-2">
          <p className="flex-1 text-[11px] text-amber-200">Trocar o estilo refaz o visual pela Fase 2 (as edições desta aba não salvas se perdem).</p>
          <button type="button" className={BTN} onClick={() => setQuick({ edit: style.edit, headline: style.headline, captions: style.captions })}>Cancelar</button>
          <button type="button" className="rounded-xl bg-indigo-500 px-3 py-2 text-xs font-black text-white hover:bg-indigo-400 disabled:opacity-40" disabled={restyling} onClick={() => onRestyle({ ...style, ...quick })}>
            {restyling ? 'Enviando…' : 'Reaplicar estilo'}
          </button>
        </div>
      )}
      <div className="mt-3 flex items-center gap-3">
        <span className={LABEL}>Destaque</span>
        <input
          type="color"
          value={normHex(accent) || '#ff5200'}
          onFocus={onBegin}
          onBlur={onEnd}
          onChange={e => {
            const c = e.target.value;
            onGesture(p => ({ ...p, editData: { ...p.editData, hook: { ...p.editData.hook, accent: c }, captions: { ...p.editData.captions, accent: c } } }));
          }}
          className="h-8 w-12 cursor-pointer rounded-lg border border-white/10 bg-slate-950 p-0.5"
          aria-label="Cor de destaque"
        />
        <span className="font-mono text-[11px] text-slate-400">{(normHex(accent) || '').toUpperCase()}</span>
        <span className="text-[10px] text-slate-500">prévia na hora · entra no MP4 ao salvar</span>
      </div>
      <div className="mt-4 border-t border-white/10 pt-3">
        <span className={LABEL}>Animações · {(ed.graphics || []).length}</span>
        <AddGraphic props={props} now={now} onCommit={onCommit} onSelect={onSelect} />
      </div>
      <p className="mt-3 text-[11px] text-slate-500">Clique num chip da timeline para editar: palavra da legenda, gancho, animação ou imagem.</p>
    </Panel>
  );
}

// ─── Animações ───
const newGraphicId = () => `g${Date.now().toString(36)}${Math.floor(Math.random() * 1e4).toString(36)}`;

/** "+ adicionar animação" na agulha com seletor de template. */
function AddGraphic({ props, now, onCommit, onSelect }: {
  props: VisualProps;
  now: () => number;
  onCommit: (fn: (p: VisualProps) => VisualProps) => void;
  onSelect: (s: FinalSel) => void;
}) {
  const [open, setOpen] = useState(false);
  const ed = props.editData;
  const count = (ed.graphics || []).length;
  const full = count >= GRAPHIC_MAX_COUNT * 2; // a IA põe até 6; à mão deixamos o dobro
  const add = (template: GraphicTemplateId) => {
    const dur = ed.durationSec;
    const spec = GRAPHIC_SPECS[template];
    const len = Math.min(2.4, spec.maxDur);
    const start = Math.max(0, Math.min(dur - len, now()));
    const g: MotionGraphic = {
      id: newGraphicId(),
      template,
      start: +start.toFixed(3),
      end: +Math.min(dur, start + len).toFixed(3),
      zone: spec.defaultZone,
      props: { ...spec.defaults },
    };
    onCommit(p => ({ ...p, editData: { ...p.editData, graphics: [...(p.editData.graphics || []), g] } }));
    onSelect({ kind: 'graphic', index: count });
    setOpen(false);
  };
  const hookEnd = ed.hook.enabled ? ed.hook.endSec : 0;
  return (
    <div className="mt-2">
      {!open ? (
        <button type="button" className={BTN} disabled={full} onClick={() => setOpen(true)} data-testid="add-graphic">
          + adicionar animação na agulha
        </button>
      ) : (
        <div className="rounded-xl border border-white/10 bg-slate-900/60 p-2">
          <div className="mb-1.5 flex items-center justify-between">
            <span className="text-[11px] font-bold text-slate-300">Escolha o tipo · entra em {fmt(now())}</span>
            <button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => setOpen(false)}>cancelar</button>
          </div>
          <div className="grid grid-cols-2 gap-1.5">
            {GRAPHIC_TEMPLATES.map(t => (
              <button key={t} type="button" onClick={() => add(t)} data-template={t} className="rounded-lg border border-white/10 px-2 py-1.5 text-left hover:border-fuchsia-400/60 hover:bg-fuchsia-500/10" title={GRAPHIC_SPECS[t].description}>
                <span className="block text-[11px] font-black text-slate-100">{GRAPHIC_SPECS[t].name}</span>
                <span className="block truncate text-[10px] text-slate-500">{GRAPHIC_SPECS[t].description}</span>
              </button>
            ))}
          </div>
          {now() < hookEnd && <p className="mt-1.5 text-[10px] text-amber-300">A agulha está no gancho (até {fmt(hookEnd)}): a animação vai dividir o topo com ele.</p>}
        </div>
      )}
    </div>
  );
}

function GraphicInspector({ g, index, duration, hookEnd, videoAccent, field, onGesture, onCommit, onSelect, now }: {
  g: MotionGraphic;
  index: number;
  duration: number;
  hookEnd: number;
  videoAccent: string;
  field: { onFocus: () => void; onBlur: () => void };
  onGesture: (fn: (p: VisualProps) => VisualProps) => void;
  onCommit: (fn: (p: VisualProps) => VisualProps) => void;
  onSelect: (s: FinalSel) => void;
  now: () => number;
}) {
  const spec = isGraphicTemplate(g.template) ? GRAPHIC_SPECS[g.template] : null;
  const patchWith = (apply: typeof onGesture) => (fn: (cur: MotionGraphic) => MotionGraphic) => apply(p => {
    const arr = [...(p.editData.graphics || [])];
    if (!arr[index]) return p;
    arr[index] = fn(arr[index]);
    return { ...p, editData: { ...p.editData, graphics: arr } };
  });
  const live = patchWith(onGesture);
  const commit = patchWith(onCommit);
  const setProp = (key: string, value: unknown, how: 'live' | 'commit' = 'live') => (how === 'live' ? live : commit)(cur => {
    const next = { ...cur.props };
    if (value === undefined || value === '') delete next[key];
    else next[key] = value;
    return { ...cur, props: next };
  });
  const remove = () => {
    onCommit(p => ({ ...p, editData: { ...p.editData, graphics: (p.editData.graphics || []).filter((_, i) => i !== index) } }));
    onSelect(null);
  };
  const inHook = g.start < hookEnd;
  const tooLate = g.end > duration - 2;

  return (
    <Panel title={`Animação · ${fmt(g.start)} → ${fmt(g.end)} (${(g.end - g.start).toFixed(2)}s)`} right={<button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => onSelect(null)}>fechar</button>}>
      <label className={LABEL}>Tipo</label>
      <div className="mt-1 grid grid-cols-4 gap-1">
        {GRAPHIC_TEMPLATES.map(t => (
          <button
            key={t}
            type="button"
            title={GRAPHIC_SPECS[t].description}
            data-template={t}
            onClick={() => { if (t !== g.template) commit(cur => ({ ...cur, template: t, props: convertGraphicProps(cur.props, t) })); }}
            className={`rounded-lg border px-1.5 py-1.5 text-[10.5px] font-bold leading-tight ${t === g.template ? 'border-fuchsia-400 bg-fuchsia-500/15 text-fuchsia-100' : 'border-white/10 text-slate-400 hover:border-white/25 hover:text-slate-200'}`}
          >
            {GRAPHIC_SPECS[t].name}
          </button>
        ))}
      </div>
      {spec && <p className="mt-1.5 text-[11px] text-slate-500">{spec.description}</p>}

      {spec && (
        <div className="mt-3 space-y-2.5">
          {spec.fields.map(f => (
            <GraphicFieldEditor key={f.key} f={f} value={g.props[f.key]} videoAccent={videoAccent} field={field} setProp={setProp} />
          ))}
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        <span className={LABEL}>Posição</span>
        {spec?.selfPositioned ? (
          <span className="text-[11px] text-slate-500">a seta se posiciona pelo alvo</span>
        ) : (['top', 'middle'] as const).map(z => (
          <button key={z} type="button" onClick={() => commit(cur => ({ ...cur, zone: z }))} className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${g.zone === z ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-white/10 text-slate-400'}`}>
            {z === 'top' ? 'em cima' : 'no meio'}
          </button>
        ))}
      </div>
      {(inHook || tooLate) && (
        <p className="mt-2 text-[11px] text-amber-300">
          {inHook ? `Começa antes do fim do gancho (${fmt(hookEnd)}) — os dois disputam o topo. ` : ''}
          {tooLate ? 'Está nos 2s finais do vídeo.' : ''}
        </p>
      )}
      <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-white/10 pt-3">
        <button
          type="button"
          className={BTN}
          onClick={() => {
            const d = g.end - g.start;
            const s = Math.max(0, Math.min(duration - d, now()));
            commit(cur => ({ ...cur, start: +s.toFixed(3), end: +(s + d).toFixed(3) }));
          }}
        >
          Levar para a agulha
        </button>
        <button type="button" onClick={remove} className="rounded-lg px-2 py-2 text-[11px] font-black text-rose-300 hover:bg-rose-500/10">Remover animação</button>
      </div>
    </Panel>
  );
}

function GraphicFieldEditor({ f, value, videoAccent, field, setProp }: {
  f: GraphicField;
  value: unknown;
  videoAccent: string;
  field: { onFocus: () => void; onBlur: () => void };
  setProp: (key: string, value: unknown, how?: 'live' | 'commit') => void;
}) {
  if (f.key === 'accent') {
    const own = normalizeGraphicHex(value);
    return (
      <div className="flex items-center gap-2">
        <span className={LABEL}>Cor</span>
        <input type="color" value={own || normHex(videoAccent) || '#ff5200'} {...field} onChange={e => setProp('accent', e.target.value)} className="h-7 w-10 cursor-pointer rounded-lg border border-white/10 bg-slate-950 p-0.5" aria-label="Cor da animação" />
        {own
          ? <button type="button" className="text-[11px] font-bold text-slate-400 hover:text-white" onClick={() => setProp('accent', undefined, 'commit')}>usar destaque do vídeo</button>
          : <span className="text-[10px] text-slate-500">destaque do vídeo</span>}
      </div>
    );
  }
  switch (f.kind) {
    case 'text':
      return (
        <label className="block">
          <span className={LABEL}>{f.label}</span>
          <input className={`${INPUT} mt-1`} maxLength={f.maxLen} placeholder={f.placeholder} value={typeof value === 'string' || typeof value === 'number' ? String(value) : ''} {...field} onChange={e => setProp(f.key, e.target.value)} />
        </label>
      );
    case 'number':
      return (
        <label className="block">
          <span className={LABEL}>{f.label}</span>
          <input
            type="number"
            step={f.step ?? 'any'}
            min={f.min}
            max={f.max}
            className={`${INPUT} mt-1 font-mono`}
            value={typeof value === 'number' && Number.isFinite(value) ? value : ''}
            {...field}
            onChange={e => {
              const raw = e.target.value;
              if (raw === '') { if (!f.required) setProp(f.key, undefined); return; }
              let n = Number(raw);
              if (!Number.isFinite(n)) return;
              if (f.integer) n = Math.round(n);
              setProp(f.key, Math.max(f.min, Math.min(f.max, n)));
            }}
          />
        </label>
      );
    case 'list': {
      const items = Array.isArray(value) ? (value as unknown[]).map(v => String(v ?? '')) : [];
      return (
        <div>
          <span className={LABEL}>{f.label} ({items.length}/{f.maxItems})</span>
          <div className="mt-1 space-y-1.5">
            {items.map((it, i) => (
              <div key={i} className="flex items-center gap-1.5">
                <span className="w-4 text-right font-mono text-[10px] text-slate-500">{i + 1}</span>
                <input className={INPUT} maxLength={f.maxLen} value={it} {...field} onChange={e => { const next = [...items]; next[i] = e.target.value; setProp(f.key, next); }} />
                <button type="button" disabled={items.length <= f.minItems} onClick={() => setProp(f.key, items.filter((_, k) => k !== i), 'commit')} className="h-8 w-8 shrink-0 rounded-lg text-slate-400 hover:bg-white/10 hover:text-rose-300 disabled:opacity-30" aria-label="Remover item">×</button>
              </div>
            ))}
            {items.length < f.maxItems && (
              <button type="button" className="text-[11px] font-bold text-indigo-300 hover:text-indigo-200" onClick={() => setProp(f.key, [...items, 'Novo item'], 'commit')}>+ item</button>
            )}
          </div>
        </div>
      );
    }
    case 'select':
      return (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className={LABEL}>{f.label}</span>
          {f.options.map(o => (
            <button key={o.value} type="button" onClick={() => setProp(f.key, o.value, 'commit')} className={`rounded-lg border px-2 py-1 text-[11px] font-bold ${value === o.value ? 'border-indigo-400 bg-indigo-500/15 text-indigo-200' : 'border-white/10 text-slate-400'}`}>{o.label}</button>
          ))}
        </div>
      );
    case 'fraction': {
      const v = typeof value === 'number' ? value : 0.5;
      return (
        <label className="flex items-center gap-2">
          <span className={`${LABEL} w-32 shrink-0`}>{f.label}</span>
          <input type="range" min={f.min} max={f.max} step={0.01} value={v} {...field} onChange={e => setProp(f.key, Number(e.target.value))} className="flex-1 accent-fuchsia-400" />
          <span className="w-10 text-right font-mono text-[11px] text-slate-400">{Math.round(v * 100)}%</span>
        </label>
      );
    }
    case 'bar': {
      const bar = (value && typeof value === 'object' ? value : {}) as { label?: string; value?: number };
      return (
        <div>
          <span className={LABEL}>{f.label}</span>
          <div className="mt-1 flex gap-1.5">
            <input className={INPUT} maxLength={f.maxLen} placeholder="rótulo" value={bar.label ?? ''} {...field} onChange={e => setProp(f.key, { ...bar, label: e.target.value })} />
            <input type="number" step="any" min={0} className={`${INPUT} w-28 font-mono`} placeholder="valor" value={typeof bar.value === 'number' ? bar.value : ''} {...field} onChange={e => { const n = Number(e.target.value); if (e.target.value !== '' && Number.isFinite(n)) setProp(f.key, { ...bar, value: Math.max(0, n) }); }} />
          </div>
        </div>
      );
    }
    default:
      return null;
  }
}

